import React, { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../utils/analytics';
import './HomeShowcase.css';

/**
 * HomeShowcase — home "02 · Super Agent": the single-prompt proof, as a row of
 * ARTIFACT CARDS (Nitish, Aug 21).
 *
 * What changed and why: home used to mount <SAShowcase compact />, whose big
 * featured player AUTOPLAYS muted. We had already demoted the autoplay hero
 * video for earning ~4 unmutes a week, and then a second autoplaying player
 * sat two scrolls below it. This section's job on HOME is narrow — the super
 * agent is supporting evidence for the hire decision, not the thing being
 * evaluated — so it now proves BREADTH at a glance and nothing more. Nothing
 * plays until a visitor asks for it.
 *
 * Each card carries a MODALITY badge (Video / Document / Animation) rather
 * than a genre, because the claim being made is "every modality", and the mix
 * of badges is what makes that claim visible. Durations and the page count are
 * read from the real files, not asserted.
 *
 * Mobile: the one-line blurb is hidden (CSS), leaving thumbnail + badge +
 * title. Measured at 390px this is ~617px of section against ~760px with the
 * blurbs; a horizontal swipe row measured shorter still and was rejected —
 * breadth IS the message, and a swipe hides three of five artifacts behind a
 * gesture most visitors never make.
 *
 * /super-agents keeps the full-size showcase (SAShowcase) until the
 * prompt-to-output frame replaces it there.
 */

/* Videos open in the lightbox; the PDF opens in a new tab. Durations verified
   against the real media (ffprobe, Aug 21). */
const ARTIFACTS = [
  {
    id: 'trailblazer',
    kind: 'video',
    modality: 'Video',
    meta: '55s',
    src: '/assets/super-agents/trailblazer.mp4',
    poster: '/assets/super-agents/posters/trailblazer.jpg',
    title: 'A finished automotive ad',
    blurb: 'Shot list, footage, on-screen text and a tagline.',
  },
  {
    id: 'oscr',
    kind: 'pdf',
    modality: 'Document',
    meta: '10 pages',
    href: '/assets/super-agents/pdf/OSCR_Stock_Analysis_March_2026.pdf',
    poster: '/assets/super-agents/pdf/oscr_cover.jpg',
    title: 'A 10-page equity report',
    blurb: 'Scenario model, peer comp and risk tiers.',
  },
  {
    id: 'last_transmission',
    kind: 'video',
    modality: 'Video',
    meta: '1:12',
    src: '/assets/super-agents/last_transmission.mp4',
    poster: '/assets/super-agents/posters/last_transmission.jpg',
    title: 'The Last Transmission',
    blurb: 'Hyper-real CGI, voiceover and a title card.',
  },
  {
    id: 'tang_sanzang',
    kind: 'video',
    modality: 'Animation',
    meta: '2:52',
    src: '/assets/super-agents/tang_sanzang.mp4',
    poster: '/assets/super-agents/posters/tang_sanzang.jpg',
    title: 'The Monk\u2019s Journey',
    blurb: 'Hand-drawn anime, lip-synced and scored.',
  },
  {
    id: 'coffee',
    kind: 'video',
    modality: 'Video',
    meta: '30s',
    src: '/assets/super-agents/coffee-ad.mp4',
    poster: '/assets/super-agents/posters/coffee.jpg',
    title: 'Mountain Peak Coffee',
    blurb: 'A brand spot with music and pacing.',
  },
];

const Thumb = ({ a }) => (
  <span className="home-show__thumb">
    <img src={a.poster} alt="" loading="lazy" />
    {a.kind === 'video' ? (
      <span className="home-show__play" aria-hidden="true">
        <span>&#9654;</span>
      </span>
    ) : null}
    <span className="home-show__badge">
      {a.modality}
      <span className="home-show__dot" aria-hidden="true" />
      <em>{a.meta}</em>
    </span>
  </span>
);

const HomeShowcase = () => {
  const [active, setActive] = useState(null);
  const videoRef = useRef(null);

  const openVideo = (a) => {
    trackEvent('cta_click', {
      cta_name: 'showcase_artifact', compare_page: 'home', location: 'sa_showcase', artifact: a.id,
    });
    setActive(a);
  };
  const close = () => setActive(null);

  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add('cc-modal-open');
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      const pr = v.play();
      if (pr && pr.catch) pr.catch(() => {});
    }
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('cc-modal-open');
      document.removeEventListener('keydown', onKey);
      if (v) v.pause();
    };
  }, [active]);

  return (
    <section className="home-show" id="sa-showcase">
      <div className="home-show__head">
        {/* A real H2 (not H1): the page keeps exactly one H1 — the hero's
            keyword headline. */}
        <h2 className="home-show__title aie-display">Deep reasoning. Every modality.</h2>
        <p className="home-show__sub">
          An ad, an equity report, a sci-fi short, an anime. Every
          piece here was created from a single prompt.
        </p>
      </div>

      <div className="home-show__cards">
        {ARTIFACTS.map((a) => (a.kind === 'pdf' ? (
          <a
            key={a.id}
            className="home-show__card"
            href={a.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('cta_click', {
              cta_name: 'showcase_artifact', compare_page: 'home', location: 'sa_showcase', artifact: a.id,
            })}
          >
            <Thumb a={a} />
            <span className="home-show__body">
              <span className="home-show__ctitle">{a.title}</span>
              <span className="home-show__blurb">{a.blurb}</span>
            </span>
          </a>
        ) : (
          <button key={a.id} type="button" className="home-show__card" onClick={() => openVideo(a)}>
            <Thumb a={a} />
            <span className="home-show__body">
              <span className="home-show__ctitle">{a.title}</span>
              <span className="home-show__blurb">{a.blurb}</span>
            </span>
          </button>
        )))}
      </div>

      <p className="home-show__fineprint">
        Illustrative examples generated by CellCog users from a single prompt.
        Brand names and logos are the property of their respective owners and
        do not imply any affiliation or endorsement.
      </p>

      {/* the lightbox — [hidden] paired with display:none in CSS (an unpaired
          display rule is how a hidden overlay ends up eating every click) */}
      <div className="home-show__light" hidden={!active}>
        <div className="home-show__scrim" onClick={close} role="presentation" />
        <div
          className="home-show__lightcard"
          role="dialog"
          aria-modal="true"
          aria-label={active ? active.title : 'Artifact'}
        >
          <button type="button" className="home-show__x" onClick={close} aria-label="Close">
            &#10005;
          </button>
          {/* mounts only while open, so the closed page never holds an armed
              <video> (the prerender-safety doctrine) */}
          {active ? (
            <video
              ref={videoRef}
              controls
              playsInline
              preload="auto"
              poster={active.poster}
              src={active.src}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default HomeShowcase;
